import { IFuelTypes, IPlanContentMatcher } from '../../Energy';
import { ODSAlertTones, UIOptions } from './UIOptions/uiOptions.types';
import { STATE } from '../../types';
import React, { ReactElement, ReactNode } from 'react';
import { SvgIconProps } from '@origin-digital/ods-icons';

type PartialRecord<K extends keyof any, T> = Partial<Record<K, T>>;

export type DEFAULT_STATE = 'defaultState';
export type DEFAULT_FUEL = 'defaultFuel';

export type PerkAccordionDataId =
  | 'edr-perk-accordion'
  | 'ev-perk-accordion'
  | 'usage-rate-accordion'
  | 'rate-accordion'
  | 'plan-credit-accordion'
  | 'internet-offer-perk'
  | 'battery-starter-perk'
  | 'battery-maximiser-perk'
  | 'battery-lite-about-plan-perk'
  | 'battery-lite-perk'
  | 'shift-plan-perk';

export type StaticPerkAccordionType = {
  accordionSummary: string | ReactNode;
  accordionIcon: string;
  accordionBody: React.ReactNode;
  accordionDataId: PerkAccordionDataId;
  expanded?: boolean;
  synced?: boolean;
};

export type DiscountPerkAccordionType = {
  accordionSummary: ({ percentage }: RatePercentage) => React.ReactNode;
  accordionIcon: string;
  accordionBody: ({ percentage }: RatePercentage) => React.ReactNode;
  accordionDataId: PerkAccordionDataId;
  expanded?: boolean;
  synced?: boolean;
};

export type PlanCreditAccordionType = {
  accordionSummary: React.ReactNode;
  accordionIcon: string;
  accordionBody: React.ReactNode;
  accordionDataId: PerkAccordionDataId;
  expanded?: boolean;
  synced?: boolean;
};

export type RatePercentage = {
  percentage: number;
};

export type PlanCreditPerk = {
  isQLD: boolean;
};

export type PlanCreditPerkPromoBox = {
  accordionSummary: React.ReactNode;
  accordionIcon: string;
  accordionBody: React.ReactNode;
  accordionDataId: PerkAccordionDataId;
  expanded?: boolean;
  synced?: boolean;
};

export type SalesFormEDRPerkAccordionType = {
  accordionSummary: (edrPoints: string, fuelType: string) => React.ReactNode;
  accordionIcon: string;
  accordionBody: (edrPoints: string, fuelType: string) => React.ReactNode;
  accordionDataId: PerkAccordionDataId;
  expanded?: boolean;
  synced?: boolean;
};

// SalesFormPerkAccordionType would be a union of different Sales form Perk types
export type SalesFormPerkAccordionType = SalesFormEDRPerkAccordionType;

export type PerkAccordionType =
  | StaticPerkAccordionType
  | DiscountPerkAccordionType
  | PlanCreditAccordionType;

export type IOnlineCredits = {
  tone: ODSAlertTones;
  heading: string;
  content: string;
};

export type ContentRoot = {
  keyFeatures?: IContentDefinition<(string | MarkdownItem)[]>;
  signUpOptions?: IContentDefinition<string[]>;
  signUpOptionsFootNote?: IContentDefinition<string>;
  keyDetails?: IContentDefinition<KeyDetailsItem[]>;
  keyDetailsAddons?: IContentDefinition<KeyDetailsAddonsItem[]>;
  promoBox?: IContentDefinition<React.ReactNode>;
  promoBoxAuth?: IContentDefinition<React.ReactNode>;
  onlineCredits?: IContentDefinition<IOnlineCredits>;
  perkAccordion?: IContentDefinition<PerkAccordionType[] | null>;
  perkSalesFormAccordion?: IContentDefinition<SalesFormPerkAccordionType[] | null>;
  uiOptions: IContentDefinition<UIOptions>;
  rateDiscountText?: IContentDefinition<string>;
  rateChangePrepositionText?: IContentDefinition<string>;
  tagLine?: IContentDefinition<string>;
  termsAndConditionsUrl?: IContentDefinition<string>;
  bannerContent?: IContentDefinition<string>;
  estimatesDialog: IContentDefinition<React.ReactNode>;
  solarInfo?: IContentDefinition<SolarInfo>;
};

export type MarkdownItem = {
  isMarkDown: boolean;
  content: string;
  modalContent: React.ReactNode;
  modalTitle: string;
};

export type PlanDetailsContentKey =
  | DETAILS_CONTENT_KEYS.Rates
  | DETAILS_CONTENT_KEYS.BenefitPeriod
  | DETAILS_CONTENT_KEYS.ExitFees
  | DETAILS_CONTENT_KEYS.Extras
  | DETAILS_CONTENT_KEYS.GreenOptions
  | DETAILS_CONTENT_KEYS.Addons
  | DETAILS_CONTENT_KEYS.BonusCreditsGas
  | DETAILS_CONTENT_KEYS.BonusCreditsElectricity
  | DETAILS_CONTENT_KEYS.ReferAFriendCredit
  | DETAILS_CONTENT_KEYS.CarbonNeutral
  | DETAILS_CONTENT_KEYS.EVAddOn
  | DETAILS_CONTENT_KEYS.BYOBattery
  | DETAILS_CONTENT_KEYS.TelsaEV
  | DETAILS_CONTENT_KEYS.EligibilityCriteiria;

export type KeyDetailsItem = {
  contentKey: DETAILS_CONTENT_KEYS;
  contentValue: any;
  isText?: boolean;
  modalContent?: React.ReactNode;
  /**********************************
   * New props used for mapping to *
   * sales monorepo component props *
   * ********************************/
  contentDescription?: string;
  contentIcon?: ReactElement<SvgIconProps>;
};

export type KeyDetailsAddonsItem = KeyDetailsItem;

export type SolarInfo = {
  title: string;
  content: React.ReactNode;
};

export interface IPlanContent {
  test: (plan: IPlanContentMatcher | string) => boolean;
  content: ContentRoot;
}

export type ITagState =
  | 'state:act'
  | 'state:nsw'
  | 'state:nt'
  | 'state:qld'
  | 'state:sa'
  | 'state:tas'
  | 'state:vic'
  | 'state:wa';

export type ITagFuel = 'fuel:dual' | 'fuel:electricity' | 'fuel:gas';

export type ITagConnectionScenario =
  | 'connection:scenario:acc_trf'
  | 'connection:scenario:cust_mout'
  | 'connection:scenario:cust_move'
  | 'connection:scenario:cust_prch'
  | 'connection:scenario:cust_swt'
  | 'connection:scenario:cust_vc'
  | 'connection:scenario:pros_move'
  | 'connection:scenario:pros_swt'
  | 'connection:scenario:pros_vc';

export type ITagCampaignId = `campaignId:${string}`;

export type ITag = ITagState | ITagFuel | ITagConnectionScenario | ITagCampaignId;

export type IContentRecord<T> = {
  data: T;
  tags: ITag[];
};

export type IContentsByTag<K> = {
  contents?: IContentRecord<K>[];
};

export type IContentDefinition<K> = PartialRecord<STATE, IContentPerFuel<K>> &
  PartialRecord<DEFAULT_STATE, IContentPerFuel<K>> &
  IContentsByTag<K>;

export type IContentPerFuel<K> = PartialRecord<IFuelTypes, K> & Record<DEFAULT_FUEL, K>;

export type ContentRootKey = keyof ContentRoot;

export enum DETAILS_CONTENT_KEYS {
  Rates = 'Rates',
  BenefitPeriod = 'Benefit period',
  GreenOptions = 'Green options',
  ExitFees = 'Exit fees',
  Extras = 'Extras',
  BonusCreditsElectricity = 'Bonus electricity credit',
  BonusCreditsGas = 'Bonus natural gas credit',
  ReferAFriendCredit = 'Refer a friend credit',
  CarbonNeutral = 'Carbon offset add-ons',
  Addons = 'Add-ons',
  TelsaEV = 'Tesla EV charging - 8c/kWh',
  EVAddOn = 'EV charging - 8c/kWh',
  BYOBattery = 'BYO battery for extra value',
  EligibilityCriteiria = 'Eligibility criteria applies',
}

export enum PLAN_DETAILS_KEY {
  RateType = 'Rate type',
  ExitFees = 'Exit fees',
  PlanPeriod = 'Plan period',
  BillingType = 'Billing type',
}
